// De-duplicated catalog of every metric selectable in a custom report. It is
// derived entirely from `KPI_METRIC_GROUPS` (the canonical KPI dashboard
// registry) so the picker only ever offers metrics that already exist on the
// KPI dashboard and are already fetched by the existing cube queries — no new
// cube queries are introduced by custom reports.
//
// Dynamic groups (revenueBreakdown / totalBusinessRevenue / gaRevenue) resolve
// their metrics from prefs at runtime, so we enumerate them across a small grid
// of representative prefs and union the results to capture every metric they
// can ever emit.

import {
  CALC_KEY_PREFIX,
  calcDefsById,
  calcFormatToMetricType,
  collectCalcLeafKeys,
  type CalcFormat,
  type CalculatedMetricDef,
} from './calculated-metrics';
import { INITIATIVE_CATEGORY_KEY_PREFIX, INITIATIVE_TYPE_KEY_PREFIX } from './initiative-metrics';
import { KPI_METRIC_GROUPS, type KpiTableSlug } from './metric-groups';
import type { MetricDefinition, MetricFormatType, ResolvedKpiPrefs } from './types';

/**
 * Where a metric's value comes from. Derived from the metric key prefix. Most
 * are cube queries; `computed` metrics are derived in JS from other measures +
 * Financial Settings (computed-metrics.ts); `calculated` metrics are user-defined
 * formulas stored per-report (calculated-metrics.ts); `custom` metrics are
 * user-entered values stored per-company (CompanyCustomMetric tables). None of
 * computed/calculated/custom reads a cube column. `initiatives` metrics read
 * the adset media cube grouped by initiative_type, pivoted on the frontend
 * onto `initiative.*` / `initiative_category.*` synthetic keys
 * (initiative-metrics.ts).
 */
export type MetricSource = 'metrics' | 'brand_value' | 'media_campaigns' | 'computed' | 'calculated' | 'custom' | 'initiatives';

/** Prefix for user-defined custom-metric keys: `custom.<metricId>`. */
export const CUSTOM_KEY_PREFIX = 'custom.';

export function isCustomMetricKey(key: string): boolean {
  return key.startsWith(CUSTOM_KEY_PREFIX);
}

/**
 * Minimal shape of a company custom-metric definition needed to synthesize a
 * catalog entry. Mirrors the API's CompanyCustomMetricDto (id/name/format)
 * without the shared lib depending on the app's API client.
 */
export interface CustomMetricLike {
  id: string;
  name: string;
  format: CalcFormat;
}

export interface MetricCatalogEntry {
  key: string;
  label: string;
  type: MetricFormatType;
  suffix?: string;
  /** Title of the group the metric canonically belongs to — used to group the picker. */
  category: string;
  /** Source cube query, derived from the key prefix. */
  source: MetricSource;
}

const BRAND_VALUE_KEY_PREFIX = 'company_platform_brand_value.';
const MEDIA_CAMPAIGNS_KEY_PREFIX = 'company_media_metrics_campaigns.';
const COMPUTED_KEY_PREFIX = 'computed.';

export function sourceForMetricKey(key: string): MetricSource {
  if (key.startsWith(BRAND_VALUE_KEY_PREFIX)) return 'brand_value';
  if (key.startsWith(MEDIA_CAMPAIGNS_KEY_PREFIX)) return 'media_campaigns';
  if (key.startsWith(COMPUTED_KEY_PREFIX)) return 'computed';
  if (key.startsWith(CALC_KEY_PREFIX)) return 'calculated';
  if (key.startsWith(CUSTOM_KEY_PREFIX)) return 'custom';
  if (key.startsWith(INITIATIVE_TYPE_KEY_PREFIX) || key.startsWith(INITIATIVE_CATEGORY_KEY_PREFIX)) return 'initiatives';
  return 'metrics';
}

/** Category all custom-metric entries are grouped under in the picker. */
export const CUSTOM_METRIC_CATEGORY = 'Custom Metrics';

/** Category all calculated-metric entries are grouped under in the pickers. */
export const CALCULATED_METRIC_CATEGORY = 'Calculated';

/**
 * Synthesizes catalog entries for a report's calculated metrics. Like custom
 * metrics, these are report-scoped and never live in the frozen METRIC_CATALOG
 * — callers merge them per-render into the pickers (group editor and the
 * calc-builder operand list).
 */
export function calculatedMetricCatalogEntries(defs: ReadonlyArray<CalculatedMetricDef>): MetricCatalogEntry[] {
  return defs.map((def) => ({
    key: `${CALC_KEY_PREFIX}${def.id}`,
    label: def.name,
    type: calcFormatToMetricType(def.format),
    category: CALCULATED_METRIC_CATEGORY,
    source: 'calculated' as const,
  }));
}

/**
 * The set of sources a list of metric keys actually reads, EXPANDING each
 * `calc.<id>` key into its expression's leaf sources (transitively through
 * nested calc refs). Loading gates key off this: a group whose only key is a
 * calc metric over brand-value operands must wait for the brand-value query.
 * The 'calculated' source itself is also included for any calc key.
 */
export function sourcesForMetricKeys(keys: ReadonlyArray<string>, calcDefs: ReadonlyArray<CalculatedMetricDef> = []): Set<MetricSource> {
  const defs = calcDefsById(calcDefs);
  const sources = new Set<MetricSource>();
  for (const key of keys) {
    const source = sourceForMetricKey(key);
    sources.add(source);
    if (source !== 'calculated') continue;
    const def = defs.get(key.slice(CALC_KEY_PREFIX.length));
    if (!def) continue;
    for (const leafKey of collectCalcLeafKeys(def.expression, defs)) {
      sources.add(sourceForMetricKey(leafKey));
    }
  }
  return sources;
}

/**
 * Synthesizes catalog entries for a company's custom metrics. These are
 * company-scoped and async, so they are NEVER baked into the frozen
 * METRIC_CATALOG singleton — callers merge them per-render (like report-scoped
 * calc metrics) and thread them into resolveCatalogMetrics / the pickers.
 */
export function customMetricCatalogEntries(defs: CustomMetricLike[]): MetricCatalogEntry[] {
  return defs.map((def) => ({
    key: `${CUSTOM_KEY_PREFIX}${def.id}`,
    label: def.name,
    type: calcFormatToMetricType(def.format),
    category: CUSTOM_METRIC_CATEGORY,
    source: 'custom' as const,
  }));
}

// Representative prefs permutations used to enumerate every metric the dynamic
// resolvers can emit. The resolvers only branch on depVar / amazonEnabled /
// isShopifyConnected / wholesaleEnabled, so we vary those and fill the rest
// with neutral defaults.
const CATALOG_ENUMERATION_PREFS: ResolvedKpiPrefs[] = (() => {
  const depVars = ['shopify_subscription', 'shopify', 'ga_'];
  const flags = [true, false];
  const grid: ResolvedKpiPrefs[] = [];
  for (const depVar of depVars) {
    for (const amazonEnabled of flags) {
      for (const isShopifyConnected of flags) {
        for (const wholesaleEnabled of flags) {
          grid.push({
            depVar,
            depVarScaler: [],
            amazonEnabled,
            isAmazonConnected: amazonEnabled,
            isShopifyConnected,
            baselineRevenueMetric: '',
            isGaMultiPropertyEnabled: false,
            wholesaleEnabled,
          });
        }
      }
    }
  }
  return grid;
})();

function metricsForGroup(slug: KpiTableSlug): MetricDefinition[] {
  const group = KPI_METRIC_GROUPS[slug];
  // `resolveMetrics` is only present on the dynamic variant; keying off it (the
  // same approach `resolveGroupMetrics` uses) narrows the union correctly.
  if ('resolveMetrics' in group) {
    const seen = new Set<string>();
    const out: MetricDefinition[] = [];
    for (const prefs of CATALOG_ENUMERATION_PREFS) {
      for (const metric of group.resolveMetrics(prefs)) {
        if (!seen.has(metric.key)) {
          seen.add(metric.key);
          out.push(metric);
        }
      }
    }
    return out;
  }
  return [...group.metrics];
}

/**
 * Builds the de-duplicated metric catalog. When the same key appears in more
 * than one group, the first occurrence (in `KPI_METRIC_GROUPS` declaration
 * order) wins for label/type/category, so every key maps to exactly one
 * canonical entry.
 */
export function buildMetricCatalog(): MetricCatalogEntry[] {
  const byKey = new Map<string, MetricCatalogEntry>();

  for (const slug of Object.keys(KPI_METRIC_GROUPS) as KpiTableSlug[]) {
    const category = KPI_METRIC_GROUPS[slug].title;
    for (const metric of metricsForGroup(slug)) {
      if (byKey.has(metric.key)) {
        continue;
      }
      byKey.set(metric.key, {
        key: metric.key,
        label: metric.label,
        type: metric.type,
        ...(metric.suffix !== undefined ? { suffix: metric.suffix } : {}),
        category,
        source: sourceForMetricKey(metric.key),
      });
    }
  }

  return [...byKey.values()];
}

/** The de-duplicated catalog, computed once. */
export const METRIC_CATALOG: MetricCatalogEntry[] = buildMetricCatalog();

const METRIC_CATALOG_BY_KEY: Map<string, MetricCatalogEntry> = new Map(METRIC_CATALOG.map((entry) => [entry.key, entry]));

export function getMetricCatalogEntry(key: string): MetricCatalogEntry | undefined {
  return METRIC_CATALOG_BY_KEY.get(key);
}

/**
 * Resolves a list of metric keys to `MetricDefinition`s, dropping any unknown
 * keys. Used by the config-driven renderer to turn a custom group's stored
 * `metricKeys` into the metric definitions the existing row builder expects —
 * labels/types always come from the catalog (or, for `calc.<id>` keys, the
 * report's calculated-metric defs), never from the stored row config.
 *
 * `calcDefs` supplies the report-scoped calculated metrics: a `calc.<id>` key is
 * resolved to its user-set name + format (calc metrics are per-report, so they
 * are deliberately not in the global catalog).
 *
 * `extraEntries` supplies company-scoped entries that are synthesized per
 * render and never live in the frozen catalog: custom metrics
 * (`customMetricCatalogEntries`) and custom-initiative-type breakouts
 * (`initiativeTypeCatalogEntries`). Any key missing from the frozen catalog
 * falls back to these entries; still-unresolved keys are dropped.
 *
 * The frozen-catalog-first lookup relies on synthesized keys (`custom.<id>`,
 * `initiative.CUSTOM_*`) NEVER appearing in the frozen catalog — otherwise a
 * stale synthesized key would resolve against a same-named frozen entry
 * instead of dropping. The "never contains synthesized-prefix keys" spec
 * enforces that invariant.
 */
export function resolveCatalogMetrics(
  keys: string[],
  calcDefs: CalculatedMetricDef[] = [],
  extraEntries: MetricCatalogEntry[] = [],
): MetricDefinition[] {
  const calcById = new Map(calcDefs.map((def) => [def.id, def]));
  const extraByKey = new Map(extraEntries.map((entry) => [entry.key, entry]));
  const metrics: MetricDefinition[] = [];
  for (const key of keys) {
    if (key.startsWith(CALC_KEY_PREFIX)) {
      const def = calcById.get(key.slice(CALC_KEY_PREFIX.length));
      if (def) {
        metrics.push({ key, label: def.name, type: calcFormatToMetricType(def.format) });
      }
      continue;
    }
    const entry = METRIC_CATALOG_BY_KEY.get(key) ?? extraByKey.get(key);
    if (entry) {
      metrics.push({
        key: entry.key,
        label: entry.label,
        type: entry.type,
        ...(entry.suffix !== undefined ? { suffix: entry.suffix } : {}),
      });
    }
  }
  return metrics;
}
